import React, { useState, Fragment } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Stack,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  DialogContentText,
  Chip,
  Collapse,
  IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import GetAppIcon from "@mui/icons-material/GetApp";
import ScienceIcon from "@mui/icons-material/Science";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InfoIcon from "@mui/icons-material/Info";
import { Tooltip } from "@mui/material";

import UpdateQuestionnaireTests from "../PageUtils/Admin/UpdateQuestionnaireTests";
import UpdateUsers from "../PageUtils/Admin/UpdateUsers";
import AddSingleUserDialog from "../PageUtils/Admin/AddSingleUserDialog";
import TopNav from "../PageUtils/User/TopNav/TopNav";
import {
  downloadQuestionnaireTests,
  downloadUsersResults,
  downloadUsersAttended,
  downloadFullDB,
  getAdminUsers,
  downloadUsersAsCsv,
  downloadCalculatedResults,
  getPreviewResults,
} from "./AdminAPI/AdminGet";

import {
  postLoadUsersFromResults,
  postTestUsersQuestions,
  postUpdateTests,
  postUpdateUsers,
  postDeleteResponses,
  postDeleteAttendedUsers,
  postDeleteSingleUser,
  postDeleteAllUsers,
  postAddSingleUser
} from "./AdminAPI/AdminPost";

const DELETE_ALL_CONFIRM_PHRASE = "DELETE ALL USERS";

const formatDate = (dateValue) => {
  if (!dateValue) return "N/A";

  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return dateValue;

  const monthName = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();

  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  return `${monthName} ${getOrdinal(date.getDate())} ${date.getFullYear()}`;
};

const UsersTableDialog = ({ open, onClose, users, title, onConfirm, confirmText, subtitle, isQuestionnaireMissing }) => {
  const now = new Date();

  const processedUsers = users.map(user => {
    const expiryDate = user.expires ? new Date(user.expires) : null;
    const isInvalid = !user.expires || isNaN(new Date(user.expires).getTime());
    const isExpired = !isInvalid && expiryDate && expiryDate < now;

    const raw = user.originalExpires || "";
    // isAmbiguous: Exactly XX/YY/ZZZZ or XX-YY-ZZZZ
    const isAmbiguous = /^\d{1,2}[/-]\d{1,2}[/-]\d{4}$/.test(raw);
    // isNonStandard: Not YYYY-MM-DD
    const isNonStandard = raw && !/^\d{4}-\d{2}-\d{2}$/.test(raw);

    return { ...user, isInvalid, isExpired, isAmbiguous, isNonStandard };
  }).sort((a, b) => {
    if (a.isInvalid !== b.isInvalid) return a.isInvalid ? -1 : 1;
    if (a.isExpired !== b.isExpired) return a.isExpired ? -1 : 1;
    return 0;
  });

  const invalidCount = processedUsers.filter(u => u.isInvalid).length;
  const expiredCount = processedUsers.filter(u => u.isExpired).length;
  const ambiguousCount = processedUsers.filter(u => u.isAmbiguous).length;
  const nonStandardCount = processedUsers.filter(u => u.isNonStandard).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.5rem", px: 4, pt: 4, pb: 2 }}>{title}</DialogTitle>
      <DialogContent sx={{
        px: 3,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        // Default: Only the table scrolls.
        // Fallback: If the screen is too short, let the whole content scroll.
        overflow: 'hidden',
        '@media (max-height: 600px)': {
          overflowY: 'auto',
          display: 'block'
        }
      }}>
        <Box sx={{ flexShrink: 0, mb: 2, pt: 1 }}>
          <Stack spacing={1}>
            {subtitle && (
              <Typography variant="body1" sx={{ mb: 2, px: 1, color: "text.secondary" }}>
                {subtitle}
              </Typography>
            )}
            {invalidCount > 0 && (
              <Alert severity="error" sx={{ fontWeight: "bold" }}>
                Error: {invalidCount} user{invalidCount > 1 ? "s have" : " has"} invalid dates!
              </Alert>
            )}
            {expiredCount > 0 && (
              <Alert severity="warning" sx={{ fontWeight: "bold" }}>
                Warning: {expiredCount} user{expiredCount > 1 ? "s" : ""} {expiredCount > 1 ? "have" : "has"} expired dates!
              </Alert>
            )}
            {ambiguousCount > 0 && (
              <Alert severity="warning" sx={{ fontWeight: "bold" }}>
                Ambiguity Warning: {ambiguousCount} date{ambiguousCount > 1 ? "s use" : " uses"} the XX/YY/ZZZZ or XX-YY-ZZZZ format, which can be confusing (DD/MM vs MM/DD).
              </Alert>
            )}
            {nonStandardCount > 0 && (
              <Alert severity="info" sx={{ fontWeight: "bold" }}>
                Tip: {nonStandardCount} date{nonStandardCount > 1 ? "s are" : " is"} in a non-standard format. We recommend using YYYY-MM-DD.
              </Alert>
            )}
            {isQuestionnaireMissing && (
              <Alert severity="warning" sx={{ fontWeight: "bold" }}>
                Warning: No questionnaire column was found in the file. All users will default to <b>Ahamnon</b>.
              </Alert>
            )}
          </Stack>
        </Box>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            minHeight: 200,
            '@media (max-height: 600px)': {
              flexGrow: 0,
              overflow: 'visible',
              maxHeight: 'none'
            }
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem", bgcolor: "grey.200", p: 2 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem", bgcolor: "grey.200", p: 2 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem", bgcolor: "grey.200", p: 2 }}>Gender</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem", bgcolor: "grey.200", p: 2 }}>Questionnaire</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem", bgcolor: "grey.200", p: 2 }}>Expires</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedUsers.map((user, index) => (
                <TableRow key={index} hover>
                  <TableCell sx={{ fontSize: "1.1rem", p: 2 }}>{user.id}</TableCell>
                  <TableCell sx={{ fontSize: "1.1rem", p: 2 }}>{user.name}</TableCell>
                  <TableCell sx={{ fontSize: "1.1rem", p: 2 }}>{user.gender || "-"}</TableCell>
                  <TableCell sx={{ fontSize: "1.1rem", p: 2 }}>
                    {typeof user.questionnaire === 'object' ? user.questionnaire.questionnaireName : (user.questionnaire === 'kodkod' ? 'קודקוד' : (user.questionnaire === 'ahamnon' ? 'אחמנון' : (user.questionnaire === 'bogrim' ? 'בוגרים' : (user.questionnaire === 'mapel' ? 'מייפל' : user.questionnaire))))}
                  </TableCell>
                  <TableCell sx={{ fontSize: "1.1rem", p: 2, color: user.isInvalid || user.isExpired ? "error.main" : "inherit", fontWeight: user.isInvalid || user.isExpired ? "bold" : "normal" }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {user.isInvalid && (
                        <Tooltip title="Invalid date format">
                          <WarningIcon fontSize="small" color="error" />
                        </Tooltip>
                      )}
                      {user.isExpired && (
                        <Tooltip title="Date expired">
                          <WarningIcon fontSize="small" color="error" />
                        </Tooltip>
                      )}
                      {user.isAmbiguous && (
                        <Tooltip title="Ambiguous format (XX/YY/ZZZZ). Use YYYY-MM-DD instead.">
                          <InfoIcon fontSize="small" color="warning" />
                        </Tooltip>
                      )}
                      {!user.isAmbiguous && user.isNonStandard && (
                        <Tooltip title="Non-standard format. Use YYYY-MM-DD instead.">
                          <InfoIcon fontSize="small" color="info" />
                        </Tooltip>
                      )}
                      <span>{formatDate(user.expires)}</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {processedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6, fontSize: "1.2rem", color: "text.secondary" }}>
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} color="inherit" sx={{ fontSize: "1rem" }}>Close</Button>
        {onConfirm && (
          <Button onClick={onConfirm} variant="contained" color="primary" sx={{ fontSize: "1rem", px: 4 }}>
            {confirmText || "Confirm"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const fmt = (n) => (typeof n === "number" && !isNaN(n) ? n.toFixed(1) : "-");

const RESULT_COLUMNS = [
  { key: "name", label: "Name", numeric: false },
  { key: "dna_grade", label: "DNA", numeric: true },
  { key: "lecture_grade", label: "Lecture", numeric: true },
  { key: "filesystem_grade", label: "FileSystem", numeric: true },
  { key: "workshop_grade", label: "Grade", numeric: true },
  { key: "workshop_progress", label: "Progress", numeric: true },
  { key: "workshop_final", label: "Final Score", numeric: true },
];

const CHAPTER_TESTS = [
  { key: "DNA", chapters: 4 },
  { key: "Lecture", chapters: 3 },
  { key: "FileSystem", chapters: 3 },
];

const ChapterRow = ({ user }) => (
  <TableRow>
    <TableCell colSpan={RESULT_COLUMNS.length + 1} sx={{ py: 0, bgcolor: "grey.50" }}>
      <Collapse in unmountOnExit>
        <Box sx={{ px: 4, py: 1.5 }}>
          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {CHAPTER_TESTS.map(({ key, chapters }) => (
              <Box key={key}>
                <Typography variant="caption" sx={{ fontWeight: "bold", color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {key}
                </Typography>
                <Table size="small" sx={{ mt: 0.5 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 0.25, px: 1, fontWeight: "bold", fontSize: "0.7rem" }}>Ch</TableCell>
                      <TableCell align="right" sx={{ py: 0.25, px: 1, fontWeight: "bold", fontSize: "0.7rem" }}>Grade</TableCell>
                      <TableCell align="right" sx={{ py: 0.25, px: 1, fontWeight: "bold", fontSize: "0.7rem" }}>Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.from({ length: chapters }, (_, i) => {
                      const ch = user.chapters?.[key]?.[i];
                      const attempted = ch?.progress > 0;
                      return (
                        <TableRow key={i} sx={{ opacity: attempted ? 1 : 0.35 }}>
                          <TableCell sx={{ py: 0.25, px: 1, fontSize: "0.75rem" }}>{i + 1}</TableCell>
                          <TableCell align="right" sx={{ py: 0.25, px: 1, fontSize: "0.75rem", fontWeight: attempted ? "bold" : "normal" }}>
                            {ch ? fmt(ch.grade) : "-"}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.25, px: 1, fontSize: "0.75rem" }}>
                            {ch ? fmt(ch.progress) : "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            ))}
          </Box>
        </Box>
      </Collapse>
    </TableCell>
  </TableRow>
);

const ResultsPreviewDialog = ({ open, onClose, results }) => {
  const [sortField, setSortField] = useState("workshop_final");
  const [sortDir, setSortDir] = useState("desc");
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const sorted = [...results].sort((a, b) => {
    const aVal = sortField === "name" ? `${a.first_name} ${a.last_name}` : (a[sortField] ?? -1);
    const bVal = sortField === "name" ? `${b.first_name} ${b.last_name}` : (b[sortField] ?? -1);
    const diff = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    return sortDir === "asc" ? diff : -diff;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold" }}>
        Results Preview
        <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
          {results.length} users — click a row to expand chapters
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <TableContainer sx={{ maxHeight: 580 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: "grey.200", width: 40 }} />
                {RESULT_COLUMNS.map(col => (
                  <TableCell
                    key={col.key}
                    align={col.numeric ? "right" : "left"}
                    sx={{ fontWeight: "bold", bgcolor: "grey.200" }}
                  >
                    <TableSortLabel
                      active={sortField === col.key}
                      direction={sortField === col.key ? sortDir : "desc"}
                      onClick={() => handleSort(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((user, i) => {
                const isExpanded = expandedRows.has(user.id ?? i);
                return (
                  <Fragment key={user.id ?? i}>
                    <TableRow
                      hover
                      onClick={() => toggleRow(user.id ?? i)}
                      sx={{ cursor: "pointer", bgcolor: isExpanded ? "action.selected" : "inherit" }}
                    >
                      <TableCell sx={{ width: 40, p: 0.5 }}>
                        <IconButton size="small">
                          {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {user.first_name} {user.last_name}
                          {user.type && user.type !== "ahamnon" && (
                            <Chip label={user.type} size="small" color="secondary" sx={{ fontSize: "0.65rem", height: 18 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{fmt(user.dna_grade)}</TableCell>
                      <TableCell align="right">{fmt(user.lecture_grade)}</TableCell>
                      <TableCell align="right">{fmt(user.filesystem_grade)}</TableCell>
                      <TableCell align="right">{fmt(user.workshop_grade)}</TableCell>
                      <TableCell align="right">{fmt(user.workshop_progress)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold", color: "primary.main", fontSize: "1rem" }}>
                        {fmt(user.workshop_final)}
                      </TableCell>
                    </TableRow>
                    {isExpanded && <ChapterRow user={user} />}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const AdminPage = () => {
  const [loading, setLoading] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [previewUsers, setPreviewUsers] = useState([]);
  const [previewSubtitle, setPreviewSubtitle] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDialogTitle, setPreviewDialogTitle] = useState("");
  const [onConfirmPreview, setOnConfirmPreview] = useState(null);
  const [isQuestionnaireMissing, setIsQuestionnaireMissing] = useState(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [userToDeleteId, setUserToDeleteId] = useState("");
  const [deleteAllUsersDialogOpen, setDeleteAllUsersDialogOpen] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState("");
  const [addSingleUserDialogOpen, setAddSingleUserDialogOpen] = useState(false);
  const [previewResultsDialogOpen, setPreviewResultsDialogOpen] = useState(false);
  const [previewResultsData, setPreviewResultsData] = useState([]);

  const addNotification = (message, severity = "info") => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, severity, open: true }]);

    // Auto-hide: Success/info stay for 6s. Errors stay until closed manually.
    if (severity !== "error") {
      setTimeout(() => {
        handleCloseNotification(id);
      }, 6000);
    }
  };

  const handleCloseNotification = (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, open: false } : n));
    // Remove from array after transition
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 300);
  };

  const handleAction = async (actionName, actionFn, successMessage, showSuccessMessage = true) => {
    setLoading(prev => ({ ...prev, [actionName]: true }));
    try {
      const response = await actionFn();
      if (showSuccessMessage) {
        const message = successMessage || (response && typeof response.data === "string" ? response.data : "Action completed successfully");
        addNotification(message, "success");
      }
      return response;
    } catch (error) {
      console.error(error);
      let errorMessage = "Action failed";

      if (error.response && error.response.data) {
        const data = error.response.data;
        if (data instanceof Blob && data.type === "application/json") {
          // Try to parse JSON error from Blob
          try {
            const text = await data.text();
            const json = JSON.parse(text);
            errorMessage = json.message || json.error || text;
          } catch (e) {
            errorMessage = await data.text();
          }
        } else if (data instanceof Blob) {
          // Try to read Blob as text
          try {
            errorMessage = await data.text();
          } catch (e) {
            errorMessage = "Failed to parse error response";
          }
        } else {
          errorMessage = typeof data === "string" ? data : JSON.stringify(data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      addNotification(errorMessage, "error");
    } finally {
      setLoading(prev => ({ ...prev, [actionName]: false }));
    }
  };

  const handlePreviewUsersFromCSV = (users, filename, questionnaireMissing) => {
    setPreviewUsers(users);
    setPreviewSubtitle(
      <>Do you want to load <b>{users.length}</b> users from <i>"{filename}"</i>?</>
    );
    setPreviewDialogTitle("Preview: Users to Upload");
    setOnConfirmPreview(() => () => handleUploadUsers(users));
    setIsQuestionnaireMissing(!!questionnaireMissing);
    setPreviewDialogOpen(true);
  };

  const handleViewCurrentUsers = async () => {
    const users = await handleAction("fetchUsers", getAdminUsers, null, false);
    if (users) {
      setPreviewUsers(users);
      setPreviewSubtitle(`Currently loaded users in server (${users.length} total)`);
      setPreviewDialogTitle("Current Loaded Users");
      setOnConfirmPreview(null);
      setIsQuestionnaireMissing(false);
      setPreviewDialogOpen(true);
    }
  };

  const handleUploadUsers = async (users) => {
    setPreviewDialogOpen(false);
    // Filter users to only include allowed fields for the server
    const filteredUsers = users.map(({ id, name, username, expires, questionnaire, gender }) => ({
      id, name, username, expires, questionnaire, gender
    }));
    await handleAction("updateUsers", () => postUpdateUsers({ users: filteredUsers }));
  };

  const handleAddSingleUser = async (userObj) => {
    await handleAction("addSingleUser", () => postAddSingleUser(userObj), `User ${userObj.name} added successfully`);
  };

  const handleDownloadUsersAsCsv = () => {
    handleAction("downloadUsersCsv", downloadUsersAsCsv, "Users CSV downloaded successfully");
  };

  const getUsersResults = () => {
    handleAction("getResults", downloadUsersResults, "Results downloaded successfully");
  };

  const getAttendedUsers = () => {
    handleAction("getAttended", downloadUsersAttended, "Attended users downloaded successfully");
  };

  const calculateResults = () => {
    handleAction("calculateResults", downloadCalculatedResults, "Results calculated and downloaded successfully");
  };

  const handlePreviewResults = async () => {
    const data = await handleAction("previewResults", getPreviewResults, null, false);
    if (data) {
      setPreviewResultsData(data);
      setPreviewResultsDialogOpen(true);
    }
  };

  const loadUsersFromResults = () => {
    handleAction("loadFromResults", postLoadUsersFromResults, "Users loaded from results successfully");
  };

  const loadUsersQuestions = () => {
    handleAction("testQuestions", postTestUsersQuestions, "Questions tested successfully");
  };

  const deleteResponses = () => {
    if (window.confirm("Are you sure you want to delete responses?")) {
      handleAction("deleteResponses", postDeleteResponses, "Responses deleted successfully");
    }
  };

  const deleteAttendedUsers = () => {
    if (window.confirm("Are you sure you want to delete attended users?")) {
      handleAction("deleteAttended", postDeleteAttendedUsers, "Attended users deleted successfully");
    }
  };

  const handleDeleteSpecificUser = async () => {
    if (!userToDeleteId.trim()) return;
    
    // We use your custom handleAction so it automatically shows the loading spinner and success/error toasts!
    const res = await handleAction("deleteSpecific", () => postDeleteSingleUser(userToDeleteId.trim()), `User ${userToDeleteId} permanently deleted`);
    
    if (res) {
      setDeleteUserDialogOpen(false);
      setUserToDeleteId("");
    }
  };

  const handleDeleteAllUsers = async () => {
    if (deleteAllConfirmText.trim() !== DELETE_ALL_CONFIRM_PHRASE) return;

    const res = await handleAction("deleteAllUsers", postDeleteAllUsers, "All users permanently deleted");

    if (res) {
      setDeleteAllUsersDialogOpen(false);
      setDeleteAllConfirmText("");
    }
  };

  const handleDownloadFullDB = () => {
    handleAction("downloadFullDB", downloadFullDB, "Database download started");
  };

  const handleDownloadTests = () => {
    handleAction("downloadTests", downloadQuestionnaireTests, "Tests downloaded successfully");
  };

  const handleUpdateTests = (zipContent) => {
    handleAction("updateTests", () => postUpdateTests(zipContent), "Tests updated successfully");
  };

  return (
    <>
      <TopNav />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" align="center" gutterBottom fontWeight="bold" sx={{ color: "black" }}>
            Admin Management
          </Typography>
          <Divider sx={{ mb: 4 }} />

          <Stack spacing={4}>
            {/* User Management Section */}
            <Box>
              <Typography variant="h6" gutterBottom color="textPrimary">
                User Management
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <UpdateUsers onPreviewUsers={handlePreviewUsersFromCSV} />
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setAddSingleUserDialogOpen(true)}
                    >
                      הוסף משתמש בודד
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={loading.fetchUsers ? <CircularProgress size={20} /> : <VisibilityIcon />}
                      onClick={handleViewCurrentUsers}
                      disabled={loading.fetchUsers}
                    >
                      View Current Users
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={loading.downloadUsersCsv ? <CircularProgress size={20} /> : <GetAppIcon />}
                      onClick={handleDownloadUsersAsCsv}
                      disabled={loading.downloadUsersCsv}
                    >
                      הורד משתמשים CSV
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={loading.getResults ? <CircularProgress size={20} /> : <GetAppIcon />}
                      onClick={getUsersResults}
                      disabled={loading.getResults}
                    >
                      Get Users Results
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={loading.getAttended ? <CircularProgress size={20} /> : <GetAppIcon />}
                      onClick={getAttendedUsers}
                      disabled={loading.getAttended}
                    >
                      Get Attended Users
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={loading.calculateResults ? <CircularProgress size={20} /> : <ScienceIcon />}
                      onClick={calculateResults}
                      disabled={loading.calculateResults}
                    >
                      Calculate Results
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={loading.previewResults ? <CircularProgress size={20} /> : <VisibilityIcon />}
                      onClick={handlePreviewResults}
                      disabled={loading.previewResults}
                    >
                      Preview Results
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Box>

            {/* Questionnaire Management Section */}
            <Box>
              <Typography variant="h6" gutterBottom color="textPrimary">
                Questionnaire Management
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                <Stack spacing={2}>
                  <UpdateQuestionnaireTests sendQuestionnaireTestsFunction={handleUpdateTests} />
                  <Box>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={loading.downloadTests ? <CircularProgress size={20} /> : <GetAppIcon />}
                      onClick={handleDownloadTests}
                      disabled={loading.downloadTests}
                    >
                      Download Questionnaire Tests
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Box>

            {/* System Tools Section */}
            <Box>
              <Typography variant="h6" gutterBottom color="textPrimary">
                System & Testing Tools
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={loading.downloadFullDB ? <CircularProgress size={20} /> : <GetAppIcon />}
                    onClick={handleDownloadFullDB}
                    disabled={loading.downloadFullDB}
                  >
                    Download Full DB
                  </Button>
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={loading.loadFromResults ? <CircularProgress size={20} /> : <ScienceIcon />}
                    onClick={loadUsersFromResults}
                    disabled={loading.loadFromResults}
                  >
                    Test Load Users From Results
                  </Button>
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={loading.testQuestions ? <CircularProgress size={20} /> : <ScienceIcon />}
                    onClick={loadUsersQuestions}
                    disabled={loading.testQuestions}
                  >
                    Test Send Questions From Users
                  </Button>
                </Stack>
              </Paper>
            </Box>

            {/* Dangerous Actions Section */}
            <Box>
              <Accordion
                disableGutters
                sx={{
                  bgcolor: "error.light",
                  color: "error.contrastText",
                  borderRadius: 1,
                  "&:before": { display: "none" }, // Remove default divider
                  "&.Mui-expanded": {
                    borderRadius: 1,
                    m: 0 // Explicitly set margin to 0 when expanded
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "error.contrastText" }} />}
                  sx={{
                    minHeight: 48,
                    "&.Mui-expanded": { minHeight: 48 },
                    "& .MuiAccordionSummary-content": { m: 0, py: 1 },
                    "& .MuiAccordionSummary-content.Mui-expanded": { m: 0, py: 1 }
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WarningIcon />
                    <Typography fontWeight="bold">Dangerous Actions</Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: "background.paper", color: "text.primary", p: 2 }}>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => setDeleteUserDialogOpen(true)}
                    >
                      Delete Specific User
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => setDeleteAllUsersDialogOpen(true)}
                    >
                      Delete All Users
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={deleteResponses}
                      disabled={loading.deleteResponses}
                    >
                      {loading.deleteResponses ? <CircularProgress size={24} color="inherit" /> : "Delete All Responses"}
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={deleteAttendedUsers}
                      disabled={loading.deleteAttended}
                    >
                      {loading.deleteAttended ? <CircularProgress size={24} color="inherit" /> : "Delete Attended Users"}
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="error" sx={{ mt: 2, ml: 0, display: "block", fontWeight: "medium" }}>
                    Warning: These actions are irreversible and will delete data from the database.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Stack>
        </Paper>
      </Container>

      {/* Notification Stack */}
      <Box sx={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 2000, width: "100%", maxWidth: 600, px: 2 }}>
        <Stack spacing={1}>
          {notifications.map((n) => (
            <Alert
              key={n.id}
              onClose={() => handleCloseNotification(n.id)}
              severity={n.severity}
              variant="filled"
              sx={{
                width: "100%",
                boxShadow: 3,
                opacity: n.open ? 1 : 0,
                transition: "opacity 0.3s ease-in-out",
                visibility: n.open ? "visible" : "hidden"
              }}
            >
              {n.message}
            </Alert>
          ))}
        </Stack>
      </Box>

      <UsersTableDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        users={previewUsers}
        subtitle={previewSubtitle}
        title={previewDialogTitle}
        onConfirm={onConfirmPreview}
        confirmText="Upload Users"
        isQuestionnaireMissing={isQuestionnaireMissing}
      />

      <AddSingleUserDialog
        open={addSingleUserDialogOpen}
        onClose={() => setAddSingleUserDialogOpen(false)}
        onAdd={handleAddSingleUser}
      />

      <ResultsPreviewDialog
        open={previewResultsDialogOpen}
        onClose={() => setPreviewResultsDialogOpen(false)}
        results={previewResultsData}
      />

      <Dialog open={deleteUserDialogOpen} onClose={() => setDeleteUserDialogOpen(false)}>
        <DialogTitle sx={{ color: "error.main", fontWeight: "bold" }}>Delete Specific User</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the exact ID of the user you want to permanently remove. This will wipe them from active memory, progress, states, and responses. This cannot be undone.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            label="Exact User ID"
            value={userToDeleteId}
            onChange={(e) => setUserToDeleteId(e.target.value)}
            color="error"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteUserDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteSpecificUser} 
            color="error" 
            variant="contained"
            disabled={!userToDeleteId.trim() || loading.deleteSpecific}
          >
            {loading.deleteSpecific ? <CircularProgress size={24} color="inherit" /> : "Permanently Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteAllUsersDialogOpen} onClose={() => setDeleteAllUsersDialogOpen(false)}>
        <DialogTitle sx={{ color: "error.main", fontWeight: "bold" }}>Delete All Users</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This wipes <b>every</b> user from active memory, progress, states, responses and the saved
            users list, giving the server a clean start. You will need to upload the users CSV again
            afterwards. This cannot be undone.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            Type <b>{DELETE_ALL_CONFIRM_PHRASE}</b> to confirm.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            label={DELETE_ALL_CONFIRM_PHRASE}
            value={deleteAllConfirmText}
            onChange={(e) => setDeleteAllConfirmText(e.target.value)}
            color="error"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteAllUsersDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllUsers}
            color="error"
            variant="contained"
            disabled={deleteAllConfirmText.trim() !== DELETE_ALL_CONFIRM_PHRASE || loading.deleteAllUsers}
          >
            {loading.deleteAllUsers ? <CircularProgress size={24} color="inherit" /> : "Permanently Delete All"}
          </Button>
        </DialogActions>
      </Dialog>
      
    </>
  );
};

export default AdminPage;
